import { Link } from 'react-router-dom';
import { ShoppingBag, Trash2 } from 'lucide-react';
import { Drawer } from '@/components/ui/Drawer';
import { Button, ButtonLink } from '@/components/ui/Button';
import { QuantitySelector } from '@/components/ui/QuantitySelector';
import { Alert } from '@/components/ui/Alert';
import { EmptyState } from '@/components/system/EmptyState';
import { BrandArtwork } from '@/components/brand/BrandArtwork';
import { useCart } from '@/providers/CartProvider';
import { usePlatform } from '@/providers/PlatformProvider';
import type { CartItem } from '@/types/cart';

/**
 * Cart drawer — BUILD-PLAN §63, §13, §41.
 *
 * §63 asks for an elegant drawer on desktop and a full-height experience on
 * mobile; the underlying <dialog> is full-height at small widths and a
 * slide-over above them, so one component serves both.
 *
 * Totals are rendered exactly as the server sends them. Shipping is described
 * rather than priced, because §86 forbids showing a number the backend has
 * not calculated — that arrives with Phase 10.
 */
export function CartDrawer() {
  const { cart, isOpen, close, isMutating, updateQuantity, remove } = useCart();
  const { isEnabled } = usePlatform();

  const items = cart?.items ?? [];
  const hasIssues = items.some((item) => item.priceChanged || item.exceedsStock);

  return (
    <Drawer
      open={isOpen}
      onClose={close}
      title={`Your Cart${cart?.itemCount ? ` (${cart.itemCount})` : ''}`}
      footer={
        items.length > 0 ? (
          <div className="flex flex-col gap-4">
            <div className="flex items-baseline justify-between">
              <span className="text-sm text-muted">Subtotal</span>
              <span className="font-display text-xl text-ink">
                {cart?.totals.subtotal.formatted}
              </span>
            </div>

            <p className="text-xs leading-relaxed text-muted">
              Shipping and any taxes are calculated at checkout.
            </p>

            {/*
              isEnabled, NOT isVisible. isVisible() is true for `coming_soon`
              — right for navigation, since a forthcoming feature should still
              have a linkable page. But an *action* button must only appear
              when the action can actually complete, or the customer is sent
              to a dead end (§86 "never create a button that pretends to
              work").
            */}
            {isEnabled('checkout') ? (
              /*
                Closed on the way out. React Router keeps the drawer mounted
                across a navigation, so without this the customer arrives at
                checkout with the cart panel still sitting over it — and has
                to dismiss a summary of what they are already looking at.
              */
              <ButtonLink to="/checkout" size="lg" fullWidth onClick={close}>
                Proceed to Checkout
              </ButtonLink>
            ) : (
              // §86: no button that pretends to work. Checkout is not open,
              // so the customer is told plainly rather than shown a dead CTA.
              <Alert tone="info">
                Online checkout opens soon.{' '}
                <Link to="/contact" className="underline underline-offset-4">
                  Enquire about these pieces
                </Link>
              </Alert>
            )}
          </div>
        ) : undefined
      }
    >
      {items.length === 0 ? (
        <EmptyState
          icon={ShoppingBag}
          title="Your cart is empty"
          description="Once you find something you love, it will appear here."
          action={
            <Button variant="secondary" onClick={close}>
              Continue Browsing
            </Button>
          }
        />
      ) : (
        <div className="flex flex-col gap-6">
          {hasIssues && (
            <Alert tone="info">
              Some items need your attention before checkout.
            </Alert>
          )}

          {items.map((item) => (
            <CartLine
              key={item.id}
              item={item}
              disabled={isMutating}
              onQuantity={(quantity) => void updateQuantity(item.id, quantity)}
              onRemove={() => void remove(item.id)}
              onNavigate={close}
            />
          ))}
        </div>
      )}
    </Drawer>
  );
}

function CartLine({
  item,
  disabled,
  onQuantity,
  onRemove,
  onNavigate,
}: {
  item: CartItem;
  disabled: boolean;
  onQuantity: (quantity: number) => void;
  onRemove: () => void;
  onNavigate: () => void;
}) {
  const personalisation = Object.entries(item.personalisation ?? {});

  return (
    <article className="flex gap-4 border-b border-line pb-6 last:border-0 last:pb-0">
      <Link
        to={`/products/${item.product.slug}`}
        onClick={onNavigate}
        className="h-24 w-20 shrink-0 overflow-hidden bg-surface-muted"
      >
        {item.product.image ? (
          <img
            src={item.product.image}
            alt={item.product.name}
            loading="lazy"
            className="h-full w-full object-cover"
          />
        ) : (
          <BrandArtwork seed={item.product.name} ratio="portrait" />
        )}
      </Link>

      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <Link
              to={`/products/${item.product.slug}`}
              onClick={onNavigate}
              className="font-display text-base leading-snug text-ink hover:text-accent"
            >
              {item.product.name}
            </Link>

            {item.variant.name && (
              <p className="mt-0.5 text-xs text-muted">{item.variant.name}</p>
            )}
          </div>

          <button
            type="button"
            onClick={onRemove}
            disabled={disabled}
            aria-label={`Remove ${item.product.name} from cart`}
            className="flex h-9 w-9 shrink-0 items-center justify-center text-muted transition-colors hover:text-danger disabled:opacity-40"
          >
            <Trash2 className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" />
          </button>
        </div>

        {/* What the customer asked for, echoed back — a personalised gift is
            the one order detail they most need to be able to check. */}
        {personalisation.length > 0 && (
          <dl className="flex flex-col gap-0.5 text-xs text-muted">
            {personalisation.map(([key, value]) => (
              <div key={key} className="flex gap-1.5">
                <dt className="capitalize">{key.replace(/_/g, ' ')}:</dt>
                <dd className="truncate text-ink">{value}</dd>
              </div>
            ))}
          </dl>
        )}

        {item.priceChanged && (
          <p className="text-xs text-warning">The price of this piece has changed.</p>
        )}

        {item.exceedsStock && (
          <p className="text-xs text-danger">
            Only {item.availableStock} remain — please reduce the quantity.
          </p>
        )}

        <div className="mt-1 flex items-center justify-between gap-3">
          <QuantitySelector
            value={item.quantity}
            onChange={onQuantity}
            min={0}
            max={item.availableStock ?? 50}
            disabled={disabled}
          />

          <span className="text-sm text-ink">{item.lineTotal.formatted}</span>
        </div>
      </div>
    </article>
  );
}
